import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from './Modal';
import Pagination from './ui/Pagination';
// The in-app confirmation. A native window.confirm freezes the browser the QA
// checks run in, and ConfirmProvider exists to retire every one of them.
import { useConfirm } from './ui/ConfirmProvider';
// The canonical number parser (docs/BUSINESS_RULES.md, "Number parsing").
// A bare Number() here would reject "1,200" and read "" as 0 — the exact two
// behaviours that owner exists to stop anyone re-inventing.
import { num } from '../utils/invoiceLineMath';
// One reading of a thrown API error, shared with the review screen. The server
// answers with a machine-readable envelope; `err.message` is prose that is not
// promised to anyone.
import { describeApiError, type DialogContent } from '../utils/errorPresentation';
import {
  listVendorItemMemory,
  setVendorItemMemoryField,
  forgetVendorItemMemory,
  importVendorItemCodeList,
  type CodeListImportReport,
  type VendorItemProfile,
  type LearnedFieldKey,
  type MemoryWeight,
} from '../lib/vendorItemMemoryApi';
// The supplier's Payment Terms live on the vendor, not in item memory
// (USC-BUG-0031); `creditDaysOnFile` is the one reading of "on file".
import { getVendor, setVendorCreditDays } from '../lib/vendorsApi';
import { creditDaysOnFile } from '../utils/paymentTerms';

/**
 * What the system has learned about one supplier's products — and the way to
 * correct or forget it.
 *
 * WHY THIS SCREEN EXISTS AT ALL. The item memory fills blank cells on every new
 * invoice from a supplier and replaces the item name our own parser derived. A
 * wrong answer in there is therefore not a one-off: it replays onto every
 * future invoice for that product, and without this panel the only way to reach
 * it would be to wait for an invoice that happens to carry the line. A memory
 * nobody can inspect is a liability, not a feature.
 *
 * It is a separate component rather than more of VendorInvoicesPage, which is
 * one of the god files CLAUDE.md says to strangle rather than grow.
 */

/** The columns the memory holds, in the order the review grid shows them. */
const FIELDS: { key: LearnedFieldKey; label: string; numeric?: boolean }[] = [
  { key: 'itemName', label: 'Item Name' },
  { key: 'itemCode', label: 'Item Code' },
  { key: 'splitUow', label: 'Pack Unit' },
  { key: 'splitPackCount', label: 'Units / Case', numeric: true },
  { key: 'splitUnitSize', label: 'Unit Size' },
  { key: 'splitItemUow', label: 'Item Unit' },
  { key: 'taxRate', label: 'VAT %', numeric: true },
];

/**
 * How strong the evidence is, said in words rather than in a number.
 *
 * The distinction is the whole basis of the memory: a value somebody typed over
 * the extractor's answer is the strongest statement anyone has made about a
 * product, and it must never be displaced by one nobody ever looked at.
 */
const WEIGHT_LABEL: Record<MemoryWeight, { text: string; className: string }> = {
  typed: { text: 'typed by a reviewer', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  confirmed: { text: 'checked against the paper', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  seen: { text: 'read off invoices, unchecked', className: 'bg-slate-50 text-slate-600 border-slate-200' },
};

/**
 * Rows per page. A supplier can hold thousands of remembered products, and the
 * server caps one page at 200; before MC-6 this asked for 100 with no pager, so
 * everything past the first 100 could only be reached by searching.
 */
export const MEMORY_PAGE_SIZE = 50;

/**
 * What an invoice line's Memory button searches the panel for (MC-6).
 *
 * The key the memory MATCHED the line on comes first. The server stamps
 * `profileMatch` on every line the memory had a hand in, and its key is an alias
 * of the very record that filled the cells, which `listProfiles` finds through
 * `aliases.key`. The printed code cannot stand in for it: a supplier that
 * renumbers a product (HT-1042 to HT-2042) is matched on the description, and
 * searching the new code finds nothing beside cells whose tooltips say they came
 * from memory. A line the memory did not touch: its code, else its name, else
 * its description.
 */
export function memorySearchForLine(row: any): string {
  return String(
    row?.profileMatch?.key || row?.itemCode || row?.itemName || row?.description || ''
  ).trim();
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  vendorId: string | null;
  vendorName?: string;
  /**
   * Opens the panel already searched: an invoice line passes
   * `memorySearchForLine(row)`, so what the memory holds for THAT product is one
   * click from the line (MC-6). Blank opens the supplier's whole list.
   */
  initialSearch?: string;
  /**
   * Whether this user may change or forget what was learned.
   *
   * Both default to FALSE. The routes gate a change on PRODUCTS edit and Forget
   * on PRODUCTS delete (Master Catalogue rights, D29) and the server refuses
   * either way, but a user shown editable cells and a Forget button gets a 403
   * for clicking what the screen offered, so the caller passes the real answer
   * from `usePermission('products')`.
   */
  canEdit?: boolean;
  canDelete?: boolean;
  /**
   * The supplier's Payment Terms row (USC-BUG-0031). The terms are NOT item
   * memory: they are stored once, on the vendor (`Vendor.creditDays`, the same
   * figure that sets every invoice's due date), and read and saved through the
   * VENDORS routes. So they are gated on VENDORS rights, not the PRODUCTS
   * rights above; the caller passes `usePermission('vendors')`. Absent = no row.
   */
  termsRights?: { canView: boolean; canEdit: boolean };
}

export const VendorItemMemoryPanel: React.FC<Props> = ({
  isOpen,
  onClose,
  vendorId,
  vendorName,
  initialSearch = '',
  canEdit = false,
  canDelete = false,
  termsRights,
}) => {
  const [rows, setRows] = useState<VendorItemProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<DialogContent | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<{ id: string; field: LearnedFieldKey } | null>(null);
  const [draft, setDraft] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const confirm = useConfirm();
  // A supplier's code list (VI-10, PriceSmart): always previewed, then written
  // only when the person presses Import on the preview.
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importReport, setImportReport] = useState<CodeListImportReport | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  // Which request the table may show. A slow answer to an older search or page
  // must not replace the answer to the one asked for since.
  const latestLoad = useRef(0);
  // The supplier's Payment Terms as on file (USC-BUG-0031): `undefined` while
  // loading, `null` when the vendor record could not be read.
  const [terms, setTerms] = useState<number | null | undefined>(undefined);
  const [termsDraft, setTermsDraft] = useState<string | null>(null);
  const [termsBusy, setTermsBusy] = useState(false);
  const canViewTerms = !!termsRights?.canView && !!vendorId;

  useEffect(() => {
    if (!isOpen || !canViewTerms || !vendorId) return;
    let live = true;
    setTerms(undefined);
    setTermsDraft(null);
    getVendor(vendorId)
      .then((v) => live && setTerms(v ? creditDaysOnFile(v.creditDays) ?? 0 : null))
      .catch(() => live && setTerms(null));
    return () => {
      live = false;
    };
  }, [isOpen, canViewTerms, vendorId]);

  async function saveTerms(raw: string) {
    if (!vendorId) return;
    const trimmed = raw.trim();
    // Blank clears the terms: 0 is how the vendor record says "not on file".
    const days = trimmed === '' ? 0 : num(trimmed);
    if (days === null || !Number.isInteger(days) || days < 0) {
      setError({
        tone: 'blocked',
        title: 'Payment Terms must be a whole number of days',
        summary: `"${raw}" is not a whole number of days, so nothing was saved.`,
        hint: 'Type the credit days, e.g. 30. Leave it blank for "not on file".',
      });
      return;
    }
    setTermsBusy(true);
    setError(null);
    try {
      const saved = await setVendorCreditDays(vendorId, days);
      setTerms(creditDaysOnFile(saved?.creditDays ?? days) ?? 0);
      setTermsDraft(null);
    } catch (e: any) {
      setError(
        describeApiError(e, {
          action: 'Save the Payment Terms',
          fallback: 'Could not save the Payment Terms for this supplier.',
        })
      );
    } finally {
      setTermsBusy(false);
    }
  }

  const load = useCallback(
    async (term: string, pageNo: number) => {
      if (!vendorId) return;
      const mine = ++latestLoad.current;
      setLoading(true);
      setError(null);
      try {
        const res = await listVendorItemMemory({
          vendorId,
          search: term,
          page: pageNo,
          limit: MEMORY_PAGE_SIZE,
        });
        if (mine !== latestLoad.current) return;
        setRows(res.data || []);
        setTotal(res.meta?.total ?? (res.data || []).length);
      } catch (e: any) {
        if (mine !== latestLoad.current) return;
        setError(
          describeApiError(e, {
            action: 'Load this supplier’s item memory',
            fallback: 'Could not load what has been learned for this supplier.',
          })
        );
      } finally {
        if (mine === latestLoad.current) setLoading(false);
      }
    },
    [vendorId]
  );

  // Every (re)open starts from the caller's search, on the first page, and with
  // nothing from the last open on screen: until this supplier's answer arrives,
  // the previous supplier's rows (and their Forget buttons) would sit under this
  // supplier's heading. Bumping `latestLoad` also disowns an answer still in
  // flight from the last open: it would otherwise land during this open's 300 ms
  // debounce, before `load` has claimed a newer number, and pass the check.
  useEffect(() => {
    if (!isOpen) return;
    latestLoad.current += 1;
    setSearch(initialSearch);
    setPage(1);
    setEditing(null);
    setRows([]);
    setTotal(0);
    setLoading(!!vendorId);
    setImportFile(null);
    setImportReport(null);
  }, [isOpen, vendorId, initialSearch]);

  // One request per settled search and page. Debounced, so typing a product
  // name is not one request per keystroke.
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => load(search, page), 300);
    return () => clearTimeout(t);
  }, [search, page, isOpen, load]);

  const heading = useMemo(
    () => `What we have learned${vendorName ? ` about ${vendorName}` : ''}`,
    [vendorName]
  );

  async function runImport(file: File, dryRun: boolean) {
    if (!vendorId) return;
    setImportBusy(true);
    setError(null);
    try {
      const res = await importVendorItemCodeList(vendorId, file, dryRun);
      setImportReport(res.data);
      if (!dryRun) {
        setImportFile(null);
        load(search, page);
      }
    } catch (e: any) {
      setImportReport(null);
      setError(
        describeApiError(e, {
          action: 'Import the code list',
          fallback: 'Could not read that code list.',
        })
      );
    } finally {
      setImportBusy(false);
    }
  }

  async function saveField(profile: VendorItemProfile, field: LearnedFieldKey, raw: string) {
    setEditing(null);
    const spec = FIELDS.find((f) => f.key === field);
    const trimmed = raw.trim();
    // Blank clears just this field; the rest of what we know about the product
    // is left alone. A number is sent as a number — the pack count is a divisor
    // downstream, not a label — and it is parsed by the owner, which reads the
    // separators and accounting forms a person actually types.
    const value = trimmed === '' ? null : spec?.numeric ? num(trimmed) : trimmed;
    if (spec?.numeric && trimmed !== '' && value === null) {
      setError({
        // "blocked", not "error": nothing was even attempted — the request was
        // never sent, so there is no failure to report, only an input to fix.
        tone: 'blocked',
        title: 'That is not a number',
        summary: `"${raw}" could not be read as a figure, so nothing was saved.`,
        hint: `${spec.label} has to be a plain number.`,
      });
      return;
    }

    setBusyId(profile._id);
    setError(null);
    try {
      const res = await setVendorItemMemoryField(profile._id, field, value);
      setRows((prev) => prev.map((r) => (r._id === profile._id ? res.data.profile : r)));
    } catch (e: any) {
      setError(
        describeApiError(e, {
          action: 'Save this correction',
          fallback: 'Could not save that correction.',
        })
      );
    } finally {
      setBusyId(null);
    }
  }

  async function forget(profile: VendorItemProfile) {
    // Forgetting discards every answer reviewers settled for this product, so
    // it confirms first, like the other destructive actions on the invoice
    // screen (delete invoice, clear split).
    const confirmed = await confirm({
      tone: 'error',
      title: `Forget everything learned about "${profile.displayName || profile.itemKey}"?`,
      summary:
        'The next confirmed invoice from this supplier will start learning it again. ' +
        'A copy of what was forgotten is kept in the audit log.',
      confirmLabel: 'Forget',
    });
    if (!confirmed) return;

    setBusyId(profile._id);
    setError(null);
    try {
      await forgetVendorItemMemory(profile._id);
      setRows((prev) => prev.filter((r) => r._id !== profile._id));
      setTotal((t) => Math.max(0, t - 1));
      // The last row of a later page: step back rather than show an empty page.
      // Otherwise, when more pages follow, reload this one: the next page's first
      // product moves up into the gap, and without the reload the page boundary
      // skips it until the panel happens to load again.
      if (rows.length === 1 && page > 1) setPage((p) => p - 1);
      else if (total > page * MEMORY_PAGE_SIZE) load(search, page);
    } catch (e: any) {
      setError(
        describeApiError(e, {
          action: 'Forget this item',
          fallback: 'Could not forget what was learned about that item.',
        })
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Modal isOpen={isOpen} title={heading} onClose={onClose} maxWidthClass="max-w-6xl" fullHeight>
      <div className="flex h-full flex-col gap-3">
        <p className="text-xs text-slate-600">
          These answers are filled in automatically on new invoices from this supplier, so nobody
          has to retype them. Quantities, prices and expiry dates are never remembered — those
          belong to a single delivery.
        </p>

        {canViewTerms && (
          <div
            className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
            data-testid="vendor-payment-terms"
          >
            <span className="font-medium text-slate-700">Payment Terms</span>
            {termsDraft !== null ? (
              <form
                className="flex flex-wrap items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!termsBusy) saveTerms(termsDraft);
                }}
              >
                <input
                  autoFocus
                  inputMode="numeric"
                  aria-label="Payment Terms (credit days)"
                  className="h-8 w-20 rounded border border-slate-300 px-2"
                  value={termsDraft}
                  onChange={(e) => setTermsDraft(e.target.value)}
                  disabled={termsBusy}
                />
                <span className="text-slate-600">days</span>
                <button
                  type="submit"
                  disabled={termsBusy}
                  className="rounded bg-primary-500 px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                >
                  {termsBusy ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  disabled={termsBusy}
                  onClick={() => setTermsDraft(null)}
                  className="rounded border border-slate-300 px-2 py-1 text-xs"
                >
                  Cancel
                </button>
              </form>
            ) : (
              <>
                <span className="text-slate-900">
                  {terms === undefined
                    ? 'Loading…'
                    : terms === null
                      ? 'Could not be read'
                      : terms > 0
                        ? `${terms} days`
                        : 'Not on file'}
                </span>
                {termsRights?.canEdit && typeof terms === 'number' && (
                  <button
                    type="button"
                    onClick={() => setTermsDraft(terms > 0 ? String(terms) : '')}
                    className="rounded border border-slate-300 px-2 py-1 text-xs"
                  >
                    Change
                  </button>
                )}
              </>
            )}
            <span className="basis-full text-xs text-slate-500">
              Stored once for this supplier, not per item. It sets the due date on every invoice from
              them.
            </span>
          </div>
        )}

        {canEdit && vendorId && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              aria-label="Choose a code list"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) {
                  setImportFile(f);
                  runImport(f, true);
                }
              }}
            />
            <button
              type="button"
              disabled={importBusy}
              onClick={() => fileInput.current?.click()}
              className="rounded-md border border-slate-300 px-3 py-1.5 font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {importBusy ? 'Reading…' : 'Import code list…'}
            </button>
            <span className="text-slate-500">
              Excel or CSV with a code column and a description column; packaging is optional. You
              see a preview before anything is saved.
            </span>
          </div>
        )}

        {importReport && (
          <div
            data-testid="code-list-report"
            className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
          >
            <div className="font-semibold">
              {importReport.dryRun ? 'Preview' : 'Imported'} {importReport.fileName}:{' '}
              {importReport.created} new, {importReport.updated} updated, {importReport.unchanged}{' '}
              unchanged
            </div>
            <div className="text-slate-500">
              Sheet “{importReport.sheetName}”, header on row {importReport.headerRow}; code in column{' '}
              {importReport.columns.code}, description in {importReport.columns.description}
              {importReport.columns.packaging
                ? `, packaging in ${importReport.columns.packaging}`
                : ', no packaging column'}
              . {importReport.rowsRead} rows read.
            </div>
            {importReport.sample && (
              <div data-testid="code-list-sample" className="text-slate-500">
                Row {importReport.sample.row} reads as: code “{importReport.sample.code}”, name “
                {importReport.sample.description}”
                {importReport.sample.packaging ? `, packaging “${importReport.sample.packaging}”` : ''}. Check
                this before importing.
              </div>
            )}
            {importReport.keptStaffAnswers.length > 0 && (
              <div className="mt-1">
                Kept {importReport.keptStaffAnswers.length} answer(s) a person typed or checked:{' '}
                {importReport.keptStaffAnswers
                  .slice(0, 10)
                  .map((k) => `${k.code} ${k.field} stays “${String(k.kept)}” (list: “${String(k.fromList)}”)`)
                  .join('; ')}
              </div>
            )}
            {importReport.duplicates.length > 0 && (
              <div className="mt-1 text-amber-800">
                Codes listed more than once:{' '}
                {importReport.duplicates
                  .slice(0, 10)
                  .map(
                    (d) =>
                      `${d.code} (rows ${d.rows.join(', ')}${d.identical ? ', identical, imported once' : ', different descriptions, SKIPPED'})`
                  )
                  .join('; ')}
              </div>
            )}
            {importReport.collisions.length > 0 && (
              <div className="mt-1 text-amber-800">
                Codes that differ only by leading zeros, kept as separate products:{' '}
                {importReport.collisions
                  .slice(0, 10)
                  .map((c) => `${c.code} / ${c.otherCode}`)
                  .join('; ')}
              </div>
            )}
            {importReport.packagingNotRead.length > 0 && (
              <div className="mt-1 text-amber-800">
                Packaging not understood, description imported without it:{' '}
                {importReport.packagingNotRead
                  .slice(0, 10)
                  .map((p) => `row ${p.row} ${p.code} “${p.packaging}”`)
                  .join('; ')}
              </div>
            )}
            {importReport.skipped.length > 0 && (
              <div className="mt-1 text-amber-800">
                Rows skipped:{' '}
                {importReport.skipped
                  .slice(0, 10)
                  .map((s) => `row ${s.row}: ${s.reason}`)
                  .join('; ')}
              </div>
            )}
            <div className="mt-2 flex gap-2">
              {importReport.dryRun && importFile && (
                <button
                  type="button"
                  disabled={importBusy || importReport.created + importReport.updated === 0}
                  onClick={() => runImport(importFile, false)}
                  className="rounded-md bg-primary-500 px-3 py-1 font-semibold text-white hover:bg-primary-600 disabled:opacity-50"
                >
                  Import into memory
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setImportReport(null);
                  setImportFile(null);
                }}
                className="rounded-md border border-slate-300 px-3 py-1 font-semibold text-slate-700 hover:bg-white"
              >
                {importReport.dryRun ? 'Cancel' : 'Close'}
              </button>
            </div>
          </div>
        )}

        <input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          aria-label="Search this supplier's item memory"
          placeholder="Search by product name or code…"
          className="w-full max-w-sm rounded-md border border-slate-200 px-3 py-1.5 text-sm"
        />

        {error && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
            <div className="font-semibold">{error.title}</div>
            {error.summary && <div className="mt-0.5">{error.summary}</div>}
            {error.hint && <div className="mt-1 text-rose-700">{error.hint}</div>}
            {error.details && (
              <details className="mt-1">
                <summary className="cursor-pointer text-xs text-rose-600">Details</summary>
                <div className="mt-0.5 font-mono text-xs">{error.details}</div>
              </details>
            )}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto rounded-md border border-slate-200">
          <table className="data-table data-table-dense min-w-[900px]">
            <thead className="sticky top-0 bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 font-semibold">Product</th>
                {FIELDS.map((f) => (
                  <th key={f.key} className="px-3 py-2 font-semibold">
                    {f.label}
                  </th>
                ))}
                <th className="px-3 py-2 font-semibold">Seen</th>
                {canDelete && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={FIELDS.length + 3} className="px-3 py-6 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={FIELDS.length + 3} className="px-3 py-6 text-center text-slate-500">
                    {search
                      ? 'Nothing matches that search.'
                      : 'Nothing learned for this supplier yet. Correct a few cells on an invoice and confirm it — what you settle is remembered from then on.'}
                  </td>
                </tr>
              )}

              {rows.map((row) => (
                <tr key={row._id} className="border-t border-slate-100 align-top">
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-800">{row.displayName || row.itemKey}</div>
                    <div className="text-xs text-slate-400" title={(row.aliases ?? []).map((a) => `${a.kind}: ${a.key}`).join('\n')}>
                      {(row.aliases ?? []).length} way{(row.aliases ?? []).length === 1 ? '' : 's'} of recognising it
                    </div>
                  </td>

                  {FIELDS.map((f) => {
                    const block = row.fields?.[f.key];
                    const isEditing = editing?.id === row._id && editing?.field === f.key;
                    const weight = block?.weight ? WEIGHT_LABEL[block.weight] : null;

                    if (isEditing) {
                      return (
                        <td key={f.key} className="px-2 py-2">
                          <input
                            autoFocus
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onBlur={() => saveField(row, f.key, draft)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveField(row, f.key, draft);
                              if (e.key === 'Escape') setEditing(null);
                            }}
                            className="w-24 rounded border border-blue-300 px-1.5 py-1 text-xs"
                          />
                        </td>
                      );
                    }

                    return (
                      <td
                        key={f.key}
                        className={`px-3 py-2 ${canEdit ? 'cursor-text hover:bg-slate-50' : ''}`}
                        title={
                          block
                            ? `${weight?.text}. Agreed on ${block.sightings} invoice${
                                block.sightings === 1 ? '' : 's'
                              }.${canEdit ? ' Click to correct.' : ''}`
                            : canEdit
                            ? 'Nothing learned yet. Click to set it.'
                            : 'Nothing learned yet.'
                        }
                        onClick={() => {
                          if (!canEdit || busyId === row._id) return;
                          setDraft(block?.value == null ? '' : String(block.value));
                          setEditing({ id: row._id, field: f.key });
                        }}
                      >
                        {block?.value == null ? (
                          <span className="text-slate-300">—</span>
                        ) : (
                          <span
                            className={`inline-block rounded border px-1.5 py-0.5 ${
                              weight?.className || ''
                            }`}
                          >
                            {String(block.value)}
                          </span>
                        )}
                      </td>
                    );
                  })}

                  <td className="px-3 py-2 text-slate-500">{row.invoicesSeen}</td>

                  {canDelete && (
                    <td className="px-3 py-2">
                      <button
                        onClick={() => forget(row)}
                        disabled={busyId === row._id}
                        title="Forget everything learned about this product. The next confirmed invoice will start learning it again."
                        className="rounded border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50"
                      >
                        Forget
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          pageSize={MEMORY_PAGE_SIZE}
          total={total}
          noun={search ? 'matching products' : 'remembered products'}
          onPageChange={setPage}
        />
      </div>
    </Modal>
  );
};

export default VendorItemMemoryPanel;
